import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, CreditCard, RotateCcw, Truck, ShieldCheck, Package } from "lucide-react";
import ProductCard from "./ProductCard";
import { MaskedLines, Reveal } from "./Reveal";

const ICONS = { Truck, CreditCard, BadgeCheck, RotateCcw, ShieldCheck, Package };
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export const mediaUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("/api/uploads/")) return `${BACKEND}${url}`;
  if (url.startsWith("/uploads/")) return `${BACKEND}/api${url}`; // eski kayıtlar
  return url;
};

/* Her bölüm tipi mevcut Bigeldi tasarım dilini birebir korur. */

function HeroSection({ s, homeData }) {
  const st = s.settings || {};
  const img = mediaUrl(s.image) || homeData?.banners?.[0]?.image_url;
  const align = st.text_align || "LEFT";
  return (
    <section className="relative overflow-hidden bg-[#121214] text-[#FAF9F5] grain-overlay" data-testid="hero-section">
      <div className="absolute inset-0">
        {img && (
          <picture>
            {s.mobile_image && <source media="(max-width: 640px)" srcSet={mediaUrl(s.mobile_image)} />}
            <img src={img} alt={s.alt_text || s.title || "BIGELDİ"} className="w-full h-full object-cover"
              style={{ opacity: st.overlay === false ? 1 : (st.overlay_opacity ?? 45) / 100 }} />
          </picture>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#121214] via-[#121214]/40 to-transparent" />
      </div>
      <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-40 ${align === "CENTER" ? "text-center" : align === "RIGHT" ? "text-right" : ""}`}>
        {s.subtitle && <p className="font-mono-ed text-[11px] sm:text-xs uppercase tracking-[0.35em] text-amber-500 mb-6">{s.subtitle}</p>}
        <MaskedLines lines={(s.title || "").split("\n").filter(Boolean)}
          className="font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[1.03] tracking-tight text-balance" delay={0.2} />
        {s.description && <p className={`mt-6 max-w-xl text-neutral-300 text-base sm:text-lg ${align === "CENTER" ? "mx-auto" : align === "RIGHT" ? "ml-auto" : ""}`}>{s.description}</p>}
        <div className={`mt-10 flex flex-wrap gap-4 ${align === "CENTER" ? "justify-center" : align === "RIGHT" ? "justify-end" : ""}`}>
          {s.button_text && (
            <Link to={s.button_url || "/ara"} className="group inline-flex items-center gap-3 bg-amber-600 hover:bg-amber-500 text-white font-semibold px-8 h-13 py-3.5 rounded-full transition-colors" data-testid="hero-explore-button">
              {s.button_text}<ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
          {st.button2_text && (
            <Link to={st.button2_url || "/ara"} className="inline-flex items-center gap-3 border border-white/25 hover:border-white/60 px-8 py-3.5 rounded-full font-semibold transition-colors" data-testid="hero-all-products-button">
              {st.button2_text}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

function MarqueeSection({ s }) {
  const items = s.settings?.items || [];
  if (!items.length) return null;
  return (
    <div className="border-y border-[#E6E4DD] bg-white/70 backdrop-blur-md py-4 overflow-hidden" data-testid="brand-marquee">
      <div className="flex whitespace-nowrap animate-marquee font-mono-ed text-[11px] uppercase tracking-[0.3em] text-neutral-500">
        {[0, 1].map((dup) => (
          <div key={dup} className="flex shrink-0">
            {items.map((t) => (
              <span key={t + dup} className="mx-8 flex items-center gap-3">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-600 inline-block" /> {t}
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryGridSection({ s, homeData }) {
  const cats = homeData?.categories || [];
  if (!cats.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
      <Reveal>
        <div className="flex items-end justify-between mb-10">
          <div>
            {s.subtitle && <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-amber-700 mb-2">{s.subtitle}</p>}
            <h2 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight">{s.title}</h2>
          </div>
          <Link to="/ara" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold hover:text-amber-700 transition-colors">
            Tümünü gör <ArrowRight size={15} />
          </Link>
        </div>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-6">
        {cats.map((c, i) => (
          <Reveal key={c.category_id} delay={i * 0.08} className={i === 0 ? "md:col-span-8 md:row-span-2" : "md:col-span-4"}>
            <Link to={c.slug === "dadya-parfum" ? "/dadya-parfum" : `/kategori/${c.slug}`} className={`group relative block rounded-2xl overflow-hidden bg-[#F4F2EB] ${i === 0 ? "h-full min-h-[320px] md:min-h-[480px]" : "h-56"}`} data-testid={`home-category-${c.slug}`}>
              {c.image_url && <img src={mediaUrl(c.image_url)} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
              <div className="absolute bottom-0 p-6 text-white">
                <h3 className="font-display font-bold text-xl sm:text-2xl">{c.name}</h3>
                <p className="text-xs text-white/70 mt-1 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">Keşfet <ArrowRight size={13} /></p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

const AUTO_RULES = {
  discounted: (d) => d?.campaign_products,
  featured: (d) => d?.featured,
  new: (d) => d?.new_products,
  bestsellers: (d) => d?.featured,
};

function FeaturedProductsSection({ s, homeData }) {
  const st = s.settings || {};
  let products = [];
  if (st.mode === "manual" && st.product_ids?.length) {
    if (Array.isArray(s.resolved_products) && s.resolved_products.length) {
      products = s.resolved_products;
    } else {
      const all = [...(homeData?.featured || []), ...(homeData?.new_products || []), ...(homeData?.campaign_products || [])];
      products = st.product_ids.map((id) => all.find((p) => p.product_id === id)).filter(Boolean);
    }
  } else {
    products = (AUTO_RULES[st.auto_rule || "featured"] || AUTO_RULES.featured)(homeData) || [];
  }
  products = products.slice(0, st.limit || 8);
  if (!products.length) return null;
  const dark = st.theme === "dark";
  return (
    <section className={dark ? "bg-[#121214] text-[#FAF9F5] py-16 sm:py-24 grain-overlay relative" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24"} data-testid={dark ? "campaign-section" : undefined}>
      <div className={dark ? "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" : ""}>
        <Reveal>
          <div className="mb-10">
            {s.subtitle && <p className={`font-mono-ed text-[11px] uppercase tracking-[0.3em] mb-2 ${dark ? "text-amber-500" : "text-amber-700"}`}>{s.subtitle}</p>}
            <h2 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight">{s.title}</h2>
            {s.description && <p className={`text-sm mt-2 ${dark ? "text-neutral-400" : "text-neutral-500"}`}>{s.description}</p>}
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
          {products.map((p, i) => (
            <Reveal key={p.product_id} delay={Math.min(i, 7) * 0.05}>
              {dark
                ? <div className="[&>a]:bg-[#1D1E22] [&>a]:border-white/10 [&_h3]:text-white"><ProductCard product={p} index={i} /></div>
                : <ProductCard product={p} index={i} />}
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function BannerSection({ s }) {
  if (!mediaUrl(s.image)) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24">
      <Reveal>
        <Link to={s.button_url || "/ara"} className="group relative block rounded-3xl overflow-hidden h-72 sm:h-96" data-testid="secondary-banner">
          <picture>
            {s.mobile_image && <source media="(max-width: 640px)" srcSet={mediaUrl(s.mobile_image)} />}
            <img src={mediaUrl(s.image)} alt={s.alt_text || s.title || ""} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
          <div className="relative h-full flex flex-col justify-center p-8 sm:p-14 text-white max-w-lg">
            {s.title && <h3 className="font-display font-extrabold text-2xl sm:text-4xl leading-tight">{s.title}</h3>}
            {s.subtitle && <p className="mt-3 text-white/75">{s.subtitle}</p>}
            {s.button_text && (
              <span className="mt-6 inline-flex items-center gap-2 font-semibold text-amber-400">
                {s.button_text} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </span>
            )}
          </div>
        </Link>
      </Reveal>
    </section>
  );
}

function ImageTextSection({ s }) {
  const st = s.settings || {};
  const imgLeft = (st.layout || "IMAGE_LEFT") === "IMAGE_LEFT";
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
      <div className={`grid md:grid-cols-2 gap-10 items-center ${imgLeft ? "" : "md:[&>*:first-child]:order-2"}`}>
        {mediaUrl(s.image) && (
          <Reveal className="rounded-3xl overflow-hidden bg-[#F4F2EB] aspect-[4/3]">
            <img src={mediaUrl(s.image)} alt={s.alt_text || s.title || ""} loading="lazy" className="w-full h-full object-cover" />
          </Reveal>
        )}
        <Reveal delay={0.1}>
          {s.subtitle && <p className="font-mono-ed text-[11px] uppercase tracking-[0.3em] text-amber-700 mb-3">{s.subtitle}</p>}
          <h2 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight">{s.title}</h2>
          {s.description && <p className="text-sm sm:text-base text-neutral-500 mt-4 leading-relaxed">{s.description}</p>}
          {s.button_text && (
            <Link to={s.button_url || "/ara"} className="mt-6 inline-flex items-center gap-2 font-semibold text-amber-700 hover:text-amber-600">
              {s.button_text} <ArrowRight size={16} />
            </Link>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function FeatureListSection({ s }) {
  const st = s.settings || {};
  const items = st.items || [];
  if (!items.length) return null;
  if (st.variant === "manifesto") {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-28">
        {st.quote && (
          <Reveal><p className="font-serif-ed italic text-xl sm:text-2xl text-neutral-500 max-w-2xl mb-14">"{st.quote}"</p></Reveal>
        )}
        <div className="grid md:grid-cols-3 gap-10">
          {items.map((m, i) => (
            <Reveal key={m.no || i} delay={i * 0.12} className="border-t-2 border-[#121214] pt-6">
              <span className="font-display font-black text-5xl text-amber-600/20">{m.no || `0${i + 1}`}</span>
              <h3 className="font-display font-bold text-xl mt-3 mb-2">{m.title}</h3>
              <p className="text-sm text-neutral-500 leading-relaxed">{m.text}</p>
            </Reveal>
          ))}
        </div>
      </section>
    );
  }
  return (
    <section className="bg-white border-y border-[#E6E4DD] py-14" data-testid="trust-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {items.map((t, i) => {
          const Icon = ICONS[t.icon] || BadgeCheck;
          return (
            <Reveal key={t.title || i} delay={i * 0.08} className="flex gap-4">
              <div className="h-11 w-11 rounded-full bg-amber-600/10 text-amber-700 flex items-center justify-center shrink-0">
                <Icon size={20} />
              </div>
              <div>
                <p className="font-semibold text-sm">{t.title}</p>
                <p className="text-xs text-neutral-500 mt-1 leading-relaxed">{t.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

function BrandsSection({ s, homeData }) {
  const brands = homeData?.brands || [];
  if (!brands.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
        {brands.map((b) => (
          <Link key={b.brand_id} to={`/marka/${b.slug}`} className="font-display font-bold text-xl sm:text-2xl text-neutral-300 hover:text-[#121214] transition-colors" data-testid={`brand-${b.slug}`}>
            {b.name}
          </Link>
        ))}
      </div>
    </section>
  );
}

function NewsletterSection({ s }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="newsletter-section">
      <div className="bg-[#121214] text-[#FAF9F5] rounded-3xl p-10 sm:p-16 text-center grain-overlay">
        <h2 className="font-display font-extrabold text-2xl sm:text-4xl tracking-tight">{s.title || "Bülten"}</h2>
        {s.description && <p className="text-neutral-400 text-sm sm:text-base mt-4 max-w-xl mx-auto">{s.description}</p>}
        {s.button_text && (
          <Link to={s.button_url || "/ara"} className="mt-8 inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-500 text-white font-semibold px-8 py-3.5 rounded-full transition-colors">
            {s.button_text} <ArrowRight size={16} />
          </Link>
        )}
      </div>
    </section>
  );
}

function CustomHtmlSection({ s }) {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
      dangerouslySetInnerHTML={{ __html: s.settings?.html || "" }} />
  );
}

const RENDERERS = {
  HERO: HeroSection, MARQUEE: MarqueeSection, CATEGORY_GRID: CategoryGridSection,
  FEATURED_PRODUCTS: FeaturedProductsSection, CAMPAIGN: FeaturedProductsSection,
  BANNER: BannerSection, IMAGE_TEXT: ImageTextSection, FEATURE_LIST: FeatureListSection,
  BRANDS: BrandsSection, NEWSLETTER: NewsletterSection, CUSTOM_HTML: CustomHtmlSection,
};

export default function HomeSections({ sections, homeData }) {
  return (
    <>
      {sections.filter((s) => s.is_active !== false).map((s) => {
        const R = RENDERERS[s.type];
        if (!R) return null;
        return <R key={s.section_id} s={s} homeData={homeData} />;
      })}
    </>
  );
}
